import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { FaFacebook, FaWhatsapp, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { springSmooth, springBouncy, EXPO_OUT } from "../utils/animations";

const CONTACTS = [
  {
    key: "facebook",
    Icon: FaFacebook,
    href: "https://www.facebook.com/almutah.iq",
    color: "hover:bg-blue-50 dark:hover:bg-blue-500/5 hover:border-blue-300 dark:hover:border-blue-400/30",
    iconColor: "text-blue-500",
    glowHover: "group-hover:shadow-[0_0_30px_rgba(59,130,246,0.12)]",
    detail: "almutah.iq",
  },
  {
    key: "whatsapp",
    Icon: FaWhatsapp,
    href: "https://api.whatsapp.com/send/?phone=%2B9647737224466&text&type=phone_number&app_absent=0",
    color: "hover:bg-green-50 dark:hover:bg-green-500/5 hover:border-green-300 dark:hover:border-green-400/30",
    iconColor: "text-green-500",
    glowHover: "group-hover:shadow-[0_0_30px_rgba(34,197,94,0.12)]",
    detail: "+964 773 722 4466",
  },
  {
    key: "email",
    Icon: FaEnvelope,
    href: "mailto:info@almutahco.iq",
    color: "hover:bg-highlight/5 hover:border-highlight/30",
    iconColor: "text-highlight",
    glowHover: "group-hover:shadow-[0_0_30px_rgba(201,162,39,0.12)]",
    detail: "info@almutahco.iq",
  },
];

/* Contact card with 3D tilt + hover glow */
function ContactCard({ item, i }) {
  const { key, Icon, href, color, iconColor, glowHover, detail } = item;
  const { t } = useTranslation();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
    setTilt({ x: y, y: x });
  };

  return (
    <motion.a
      ref={ref}
      initial={{ opacity: 0, y: 50, filter: "blur(8px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ ...springSmooth, delay: i * 0.12 }}
      onMouseMove={onMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      animate={{ rotateX: tilt.x, rotateY: tilt.y }}
      whileHover={{ scale: 1.03 }}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ transformStyle: "preserve-3d", perspective: 800 }}
      className={`group block bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-2xl p-6 text-center transition-all duration-500 shadow-card dark:shadow-card-dark gradient-border ${color} ${glowHover}`}
    >
      <motion.div
        whileHover={{ scale: 1.2, rotate: [0, -5, 5, 0] }}
        transition={springBouncy}
        className="w-14 h-14 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:border-gray-300 dark:group-hover:border-white/20 transition-all"
      >
        <Icon className={`text-2xl ${iconColor}`} />
      </motion.div>
      <h3 className="text-lg font-bold text-main dark:text-white font-cairo mb-1">
        {t(`contact.${key}`)}
      </h3>
      <p className="text-gray-400 dark:text-white/40 text-sm font-inter" style={{direction: 'ltr'}}>{detail}</p>
    </motion.a>
  );
}

export default function ContactUs() {
  const { t } = useTranslation();
  const headingRef = useRef(null);
  const inView = useInView(headingRef, { once: true, margin: "-100px" });

  return (
    <section id="contact" className="relative bg-gray-50 dark:bg-main-light py-20 lg:py-28 overflow-hidden">
      <div className="absolute -bottom-20 start-1/3 w-96 h-96 bg-highlight/5 rounded-full blur-[80px] animate-float-orb" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div ref={headingRef} className="text-center mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.7, ease: EXPO_OUT }}
            className="text-3xl md:text-4xl font-bold text-main dark:text-white font-syne mb-4"
          >
            {t("contact.title")}
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={inView ? { scaleX: 1, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: EXPO_OUT }}
            className="w-24 h-1 bg-gradient-to-r from-transparent via-highlight to-transparent mx-auto rounded-full"
          />
        </div>

        {/* Contact cards */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          {CONTACTS.map((item, i) => (
            <ContactCard key={item.key} item={item} i={i} />
          ))}
        </div>

        {/* Map — clip-path reveal */}
        <motion.div
          initial={{ opacity: 0, y: 40, clipPath: "inset(10% 5% 10% 5% round 16px)" }}
          whileInView={{ opacity: 1, y: 0, clipPath: "inset(0% 0% 0% 0% round 16px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2, ease: EXPO_OUT }}
          className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 shadow-lg dark:shadow-card-dark"
        >
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-white/5 px-4 py-3 border-b border-gray-200 dark:border-white/10">
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <FaMapMarkerAlt className="text-highlight" />
            </motion.div>
            <h3 className="text-main dark:text-white font-cairo font-semibold">
              {t("contact.location")}
            </h3>
          </div>
          <iframe
            title="Almutahco Location"
            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d334.29952864949473!2d44.42430370605859!3d33.31030838324721!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15578191be85a1f9%3A0xa9aa55d6fd110a9f!2sHassan%20company!5e0!3m2!1sen!2sus!4v1776505697150!5m2!1sen!2sus"
            width="100%"
            height="350"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full"
          />
        </motion.div>
      </div>
    </section>
  );
}
